import React, { useEffect, useState } from 'react'
import {
  View, Text, FlatList, TouchableOpacity,
  Image, StyleSheet, Platform,
} from 'react-native'
import { ArrowLeft, Printer, Square, CheckSquare, Check } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { Spinner, YStack } from '@fliisi/pollen'

const mc = {
  primary:  '#C07A5F',
  muted:    '#FDF0EC',
  border:   '#ece8e4',
  bg:       '#faf9f7',
  card:     '#fff',
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'StorePrintTags'>
  route: RouteProp<RootStackParamList, 'StorePrintTags'>
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  price: number | null
  status: string
  barcode: string | null
  product_images: { image_url: string }[]
}

type TagSize = 'small' | 'medium' | 'large'
const TAG = {
  small:  { w: 160, h: 90,  title: 9,  price: 14, id: 7,  qr: 56  },
  medium: { w: 220, h: 120, title: 11, price: 18, id: 8,  qr: 76  },
  large:  { w: 280, h: 150, title: 13, price: 22, id: 9,  qr: 96  },
}

export default function StorePrintTagsScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { language } = useLanguage()
  const [products, setProducts]     = useState<Product[]>([])
  const [storeName, setStoreName]   = useState('')
  const [selected, setSelected]     = useState<Set<string>>(new Set())
  const [loading, setLoading]       = useState(true)
  const [tagSize, setTagSize]       = useState<TagSize>('medium')
  const [showQR, setShowQR]         = useState(true)

  useEffect(() => { load() }, [storeId])

  const load = async () => {
    setLoading(true)
    try {
      const [{ data: store }, { data: prods }] = await Promise.all([
        supabase.from('flea_markets').select('name').eq('id', storeId).single(),
        supabase
          .from('products')
          .select('id, title, title_en, title_fi, price, status, barcode, product_images(image_url)')
          .eq('flea_market_id', storeId)
          .neq('status', 'sold')
          .order('title'),
      ])
      setStoreName(store?.name || '')
      setProducts(prods || [])
      setSelected(new Set((prods || []).map((p: any) => p.id)))
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const label = (p: Product) => {
    if (language === 'fi' && p.title_fi) return p.title_fi
    if (language === 'en' && p.title_en) return p.title_en
    return p.title || '—'
  }

  const toggle = (id: string) =>
    setSelected((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })

  const toggleAll = () =>
    setSelected(selected.size === products.length ? new Set() : new Set(products.map((p) => p.id)))

  const handlePrint = () => { if (Platform.OS === 'web') window.print() }

  const qrUrl = (id: string) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://fliisi.fi/p/${id}`)}`

  const toPrint = products.filter((p) => selected.has(p.id))
  const cfg = TAG[tagSize]

  if (loading) return (
    <YStack f={1} jc="center" ai="center" backgroundColor={mc.bg}>
      <Spinner size="large" />
    </YStack>
  )

  const renderRow = ({ item }: { item: Product }) => {
    const img = item.product_images?.[0]?.image_url
    const on  = selected.has(item.id)
    return (
      <TouchableOpacity style={[s.row, on && s.rowOn]} onPress={() => toggle(item.id)} activeOpacity={0.7}>
        <View style={s.check}>
          {on ? <CheckSquare size={20} color={mc.primary} /> : <Square size={20} color="#ccc" />}
        </View>
        {img
          ? <Image source={{ uri: img }} style={s.thumb} />
          : <View style={[s.thumb, s.noImg]} />}
        <View style={s.info}>
          <Text style={s.rowTitle} numberOfLines={1}>{label(item)}</Text>
          <Text style={s.rowStatus}>{item.status}</Text>
        </View>
        <Text style={s.rowPrice}>€{item.price?.toFixed(2) ?? '—'}</Text>
      </TouchableOpacity>
    )
  }

  return (
    <View style={s.screen}>

      {/* ─── Header ─────────────────────────────── */}
      <View style={s.header} testID="no-print">
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.back}>
          <ArrowLeft size={20} color={mc.primary} />
        </TouchableOpacity>
        <View style={s.hCenter}>
          <Text style={s.hTitle}>Print Tags</Text>
          <Text style={s.hSub}>{selected.size} / {products.length} selected</Text>
        </View>
        <TouchableOpacity
          style={[s.printBtn, selected.size === 0 && s.printBtnOff]}
          onPress={handlePrint}
          disabled={selected.size === 0}
        >
          <Printer size={15} color="#fff" />
          <Text style={s.printBtnTxt}>Print</Text>
        </TouchableOpacity>
      </View>

      {/* ─── Controls ───────────────────────────── */}
      <View style={s.controls} testID="no-print">
        <TouchableOpacity style={s.chip} onPress={toggleAll}>
          <Text style={s.chipTxt}>
            {selected.size === products.length ? 'Deselect all' : 'Select all'}
          </Text>
        </TouchableOpacity>

        <View style={s.sizeRow}>
          {(['small', 'medium', 'large'] as TagSize[]).map((sz) => (
            <TouchableOpacity
              key={sz}
              style={[s.chip, tagSize === sz && s.chipActive]}
              onPress={() => setTagSize(sz)}
            >
              <Text style={[s.chipTxt, tagSize === sz && s.chipTxtActive]}>
                {sz[0].toUpperCase() + sz.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={[s.chip, showQR && s.chipQR]}
          onPress={() => setShowQR(!showQR)}
        >
          {showQR && <Check size={11} color={mc.primary} />}
          <Text style={[s.chipTxt, showQR && s.chipTxtQR]}>QR</Text>
        </TouchableOpacity>
      </View>

      {/* ─── Product list ────────────────────────── */}
      <FlatList
        testID="no-print"
        data={products}
        keyExtractor={(item) => item.id}
        renderItem={renderRow}
        style={s.list}
      />

      {/* ─── Print area ──────────────────────────── */}
      <View style={s.printArea} testID="print-area">
        {toPrint.length === 0 ? (
          <Text style={s.emptyTxt}>No items selected</Text>
        ) : (
          <View style={s.grid}>
            {toPrint.map((p) => (
              <View key={p.id} style={[s.tag, { width: cfg.w, height: cfg.h }]}>
                <View style={[s.tagStrip, { backgroundColor: mc.primary }]} />
                <View style={s.tagBody}>
                  <View style={s.tagLeft}>
                    <Text style={s.tagStore} numberOfLines={1}>{storeName}</Text>
                    <Text style={[s.tagTitle, { fontSize: cfg.title }]} numberOfLines={2}>
                      {label(p)}
                    </Text>
                    <Text style={[s.tagPrice, { fontSize: cfg.price }]}>
                      €{p.price?.toFixed(2) ?? '—'}
                    </Text>
                    <Text style={[s.tagId, { fontSize: cfg.id }]}>
                      {p.barcode ? `#${p.barcode}` : p.id.slice(-8).toUpperCase()}
                    </Text>
                  </View>
                  {showQR && (
                    <View style={s.tagQR}>
                      {/* @ts-ignore — web-only img element */}
                      <img src={qrUrl(p.id)} alt="QR" style={{ width: cfg.qr, height: cfg.qr }} />
                    </View>
                  )}
                </View>
              </View>
            ))}
          </View>
        )}
      </View>

      {/* ─── Print CSS ───────────────────────────── */}
      {Platform.OS === 'web' && (
        // @ts-ignore
        <style>{`
          @media print {
            [data-testid="no-print"]    { display: none !important; }
            [data-testid="print-area"]  { display: block !important; }
            body { margin: 0; background: #fff; }
          }
          @media screen {
            [data-testid="print-area"]  { display: none; }
          }
        `}</style>
      )}
    </View>
  )
}

const s = StyleSheet.create({
  screen:        { flex: 1, backgroundColor: '#faf9f7' },

  // Header
  header:        { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#ece8e4' },
  back:          { padding: 4, marginRight: 10 },
  hCenter:       { flex: 1 },
  hTitle:        { fontSize: 17, fontWeight: '700', color: '#222' },
  hSub:          { fontSize: 12, color: '#aaa', marginTop: 1 },
  printBtn:      { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#C07A5F', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  printBtnOff:   { opacity: 0.35 },
  printBtnTxt:   { color: '#fff', fontSize: 14, fontWeight: '600' },

  // Controls
  controls:      { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, paddingHorizontal: 16, paddingVertical: 10, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ece8e4' },
  sizeRow:       { flexDirection: 'row', gap: 4 },
  chip:          { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 5, borderRadius: 6, borderWidth: 1, borderColor: '#ddd', backgroundColor: '#f6f4f2' },
  chipActive:    { backgroundColor: '#C07A5F', borderColor: '#C07A5F' },
  chipQR:        { backgroundColor: '#FDF0EC', borderColor: '#C07A5F' },
  chipTxt:       { fontSize: 12, color: '#666', fontWeight: '500' },
  chipTxtActive: { color: '#fff' },
  chipTxtQR:     { color: '#C07A5F' },

  // List
  list:          { flex: 1 },
  row:           { flexDirection: 'row', alignItems: 'center',                       backgroundColor: '#fff', paddingVertical: 10, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: '#f0eeec' },
  rowOn:         { backgroundColor: '#FDF8F6' },
  check:         { marginRight: 12 },
  thumb:         { width: 40, height: 40, borderRadius: 6, marginRight: 12 },
  noImg:         { backgroundColor: '#eee' },
  info:          { flex: 1 },
  rowTitle:      { fontSize: 14, fontWeight: '500', color: '#222' },
  rowStatus:     { fontSize: 11, color: '#aaa', marginTop: 2, textTransform: 'capitalize' },
  rowPrice:      { fontSize: 14, fontWeight: '700', color: '#222', marginLeft: 8 },

  // Print area
  printArea:     { padding: 16 },
  emptyTxt:      { color: '#aaa', fontSize: 14, textAlign: 'center', marginTop: 32 },
  grid:          { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },

  // Tag
  tag:           { backgroundColor: '#fff', borderWidth: 1, borderColor: '#ddd', borderRadius: 4, overflow: 'hidden' },
  tagStrip:      { height: 4 },
  tagBody:       { flex: 1, flexDirection: 'row', padding: 8 },
  tagLeft:       { flex: 1, justifyContent: 'space-between' },
  tagStore:      { fontSize: 7, color: '#C07A5F', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.8 },
  tagTitle:      { fontWeight: '600', color: '#222', lineHeight: 14, marginVertical: 2 },
  tagPrice:      { fontWeight: '800', color: '#222' },
  tagId:         { color: '#bbb', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
  tagQR:         { justifyContent: 'center', alignItems: 'center', marginLeft: 6 },
})
